import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ShoppingBag, SealCheck } from '@phosphor-icons/react';
import TransparencyCard from './TransparencyCard';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';

export const ProductCard = ({ product }) => {
  const { addToCart, loading } = useCart();

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    await addToCart(product.id);
    toast.success('Added to cart', {
      description: `${product.name} has been added to your cart.`
    });
  };

  return (
    <Link to={`/products/${product.id}`} data-testid={`product-card-${product.id}`}>
      <Card className="product-card group relative overflow-hidden bg-white border border-border/40 hover:border-border transition-all duration-500">
        {/* Image */}
        <div className="relative aspect-[4/5] overflow-hidden bg-secondary">
          <img
            src={product.images?.[0] || 'https://images.pexels.com/photos/32795036/pexels-photo-32795036.jpeg'}
            alt={product.name}
            className="product-image w-full h-full object-cover"
          />
          
          {/* Transparency Score Badge */}
          <div className="absolute top-4 right-4">
            <div className="bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-md">
              <TransparencyCard score={product.transparency_score} compact />
            </div>
          </div>

          {/* Verified Badge */}
          {product.blockchain_hash && (
            <div className="absolute top-4 left-4">
              <Badge variant="secondary" className="bg-[#4A6C4C] text-white gap-1">
                <SealCheck weight="fill" className="w-3 h-3" />
                Verified
              </Badge>
            </div>
          )}

          {/* Quick Add Button */}
          <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <Button
              size="icon"
              className="h-10 w-10 rounded-full bg-primary text-primary-foreground shadow-lg hover:scale-110 transition-transform"
              onClick={handleAddToCart}
              disabled={loading}
              data-testid={`add-to-cart-${product.id}`}
            >
              <ShoppingBag weight="bold" className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="outline" className="text-xs font-normal">
              {product.category}
            </Badge>
          </div>
          <h3 className="font-medium text-foreground line-clamp-1 mb-1">{product.name}</h3>
          <p className="text-sm text-muted-foreground line-clamp-1 mb-3">{product.craftsmanship_method}</p>
          <div className="flex items-center justify-between">
            <span className="text-lg font-semibold text-foreground">${product.price?.toFixed(2)}</span>
            <span className="text-xs text-muted-foreground">{product.material}</span>
          </div>
        </div>
      </Card>
    </Link>
  );
};

export default ProductCard;
